import { useSyncExternalStore } from 'react';
import { hasNewDesign, isNewDesignOf, type UiDesign, type UserData } from 'types/user';
import { getLocalStorage, removeLocalStorage, setLocalStorage } from 'utils/localStorage';

/**
 * The design chosen while impersonating, kept in this browser and never saved: a preferences write lands in the
 * impersonated user's own document. One key, since a browser holds one session. The value names the tenant, the
 * impersonator and the impersonated user before the design, so one left by another impersonation reads as no choice.
 */
const KEY = 'crespect.impersonation-design';

type SessionUser = Pick<UserData, 'id' | 'authUserId' | 'tenants' | 'enabledFeatures' | 'preferences'>;

/** True while the session acts as someone else. */
export function isImpersonating(user: Pick<UserData, 'id' | 'authUserId'> | undefined): boolean {
    return !!user?.authUserId && user.authUserId !== user.id;
}

/** What a choice kept for the impersonation `user` is in starts with; the design follows. */
function prefixOf(user: SessionUser) {
    return `${user.tenants?.[0] ?? ''}.${user.authUserId}.${user.id}.`;
}

/**
 * What the design of `user` is resolved from, as plain values, so a cache selector that returns them re-renders its
 * reader when one changes and not on every other change to the user. `keptPrefix` is null outside impersonation.
 */
export function designInputsOf(user: SessionUser | undefined) {
    return {
        own: isNewDesignOf(user),
        offered: hasNewDesign(user),
        keptPrefix: user && isImpersonating(user) ? prefixOf(user) : null,
    };
}

const listeners = new Set<() => void>();
let listening = false;

function notify() {
    listeners.forEach((listener) => listener());
}

/**
 * Hears a kept choice change, here or in another tab. One window listener for every reader: the browser tells the
 * other tabs of a change, and `notify` tells this one.
 */
export function onKeptDesignChange(listener: () => void) {
    if (!listening) {
        listening = true;
        window.addEventListener('storage', (event) => {
            if (event.key === KEY || event.key === null) notify();
        });
    }
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

const keptFor = (keptPrefix: string | null) => (keptPrefix === null ? null : (getLocalStorage(KEY) ?? null));

function resolve({ own, offered, keptPrefix }: ReturnType<typeof designInputsOf>, kept: string | null): boolean {
    if (keptPrefix === null) return own;
    if (kept === `${keptPrefix}NEW`) return offered;
    if (kept === `${keptPrefix}CLASSIC`) return false;
    return own;
}

/**
 * Whether a user gets the new design: the choice kept here for this impersonation of them, else their own. The
 * tenant's flag applies either way. Outside impersonation the browser is not read.
 */
export function useIsNewDesignOf(inputs: ReturnType<typeof designInputsOf>): boolean {
    return resolve(
        inputs,
        useSyncExternalStore(onKeptDesignChange, () => keptFor(inputs.keptPrefix))
    );
}

/**
 * The same answer as of the call, for code outside React: the catalog `t()` reads is picked by it. What it hangs
 * on changes with the cached user and with `onKeptDesignChange`.
 */
export function isNewDesignNow(user: SessionUser | undefined): boolean {
    const inputs = designInputsOf(user);
    return resolve(inputs, keptFor(inputs.keptPrefix));
}

/** Keeps `design` for the impersonation `user` is in, for every tab of this browser. */
export function keepImpersonationDesign(user: SessionUser, design: UiDesign) {
    setLocalStorage(KEY, prefixOf(user) + design);
    notify();
}

/** Forgets the choice, so the next impersonation, the same user's included, starts in that user's own design. */
export function dropImpersonationDesign() {
    removeLocalStorage(KEY);
    notify();
}
